<template>
    <div class="users-management">
        <div class="page-header">
            <div>
                <h2 class="page-title">{{ t('customer_payment') }}</h2>
                <p class="page-subtitle">{{ t('customer_payment_subtitle') }}</p>
            </div>
        </div>

        <div class="dashboard-card">
            <div class="datatable-toolbar">
                <div class="toolbar-left">
                    <select v-model.number="perPage" class="length-select">
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>

                    <select v-model="status" class="length-select" :disabled="loading">
                        <option value="all">{{ t('all') }}</option>
                        <option value="pending">{{ t('pending') }}</option>
                        <option value="confirmed">{{ t('confirmed') }}</option>
                    </select>

                    <button type="button" class="tool-btn tool-btn-icon" @click="toggleFilters">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M2.5 4.16667H17.5M5.83333 10H14.1667M8.33333 15.8333H11.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>

                    <button type="button" class="tool-btn tool-btn-excel" @click="exportCsv">EXCEL</button>
                    <button type="button" class="tool-btn tool-btn-light" @click="printTable">PRINT</button>
                    <button type="button" class="tool-btn tool-btn-primary" @click="reloadAll" :disabled="loading">{{ t('refresh') }}</button>
                </div>

                <div class="toolbar-right">
                    <div class="search-box">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <input v-model="searchQuery" type="text" :placeholder="t('search') + '...'" class="search-input" @input="debouncedSearch" :disabled="loading">
                    </div>
                </div>
            </div>

            <div class="table-container">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>{{ t('id') }}</th>
                            <th>
                                <input v-if="showFilters" v-model="colFilters.billNo" type="text" class="th-input" :placeholder="t('bill_no')">
                                <span v-else>{{ t('bill_no') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="colFilters.billType" type="text" class="th-input" :placeholder="t('bill_type')">
                                <span v-else>{{ t('bill_type') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="colFilters.customer" type="text" class="th-input" :placeholder="t('customer')">
                                <span v-else>{{ t('customer') }}</span>
                            </th>
                            <th class="col-amount">
                                <input v-if="showFilters" v-model="colFilters.amount" type="text" class="th-input" :placeholder="t('amount')">
                                <span v-else>{{ t('amount') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="colFilters.paymentDate" type="date" class="th-input th-date">
                                <span v-else>{{ t('payment_date') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="colFilters.method" type="text" class="th-input" :placeholder="t('payment_method')">
                                <span v-else>{{ t('payment_method') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="colFilters.trx" type="text" class="th-input" :placeholder="t('trx')">
                                <span v-else>{{ t('trx') }}</span>
                            </th>
                            <th>
                                <select v-if="showFilters" v-model="status" class="th-select">
                                    <option value="all">{{ t('all') }}</option>
                                    <option value="pending">{{ t('pending') }}</option>
                                    <option value="confirmed">{{ t('confirmed') }}</option>
                                </select>
                                <span v-else>{{ t('status') }}</span>
                            </th>
                            <th>{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="10" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="row in filteredItems" v-if="!loading" :key="row.BillPaymentID">
                            <td>{{ row.BillPaymentID }}</td>
                            <td>{{ row.BillNo || '-' }}</td>
                            <td>{{ row.BillType || '-' }}</td>
                            <td>
                                <div style="display:flex; flex-direction:column;">
                                    <span style="font-weight:600;">{{ row.CustomerName || '-' }}</span>
                                    <span style="font-size:12px; color:#64748b;">{{ row.MembershipCategory || '-' }} | {{ row.MobileNo || '-' }}</span>
                                </div>
                            </td>
                            <td class="mono col-amount">{{ formatAmount(row.Amount) }}</td>
                            <td>{{ formatDateTime(row.Payment_Date) }}</td>
                            <td>{{ row.Payment_Method || '-' }}</td>
                            <td class="mono">{{ row.Transaction_ID || '-' }}</td>
                            <td>
                                <span :class="['tag', row.Payment_Status === 0 ? 'tag-warning' : 'tag-success']">
                                    {{ row.Payment_Status === 0 ? t('pending') : t('confirmed') }}
                                </span>
                            </td>
                            <td>
                                <div class="action-buttons">
                                    <button
                                        v-if="row.Payment_Status === 0"
                                        @click="openConfirm(row)"
                                        class="btn btn-primary"
                                        style="padding: 7px 10px;"
                                        :disabled="busyIds[row.BillPaymentID] === true"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                            <path d="M16.6667 5L7.5 14.1667L3.33333 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                        <span>{{ t('confirm') }}</span>
                                    </button>
                                    <span v-else class="no-access-text">-</span>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="!loading && filteredItems.length === 0">
                            <td colspan="10" style="text-align: center; padding: 24px; color: #64748b;">
                                {{ t('no_data') }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pager">
                <div class="pager-left">
                    <div class="pager-info">{{ showingText }}</div>
                </div>
                <div class="pager-right">
                    <button type="button" class="pager-btn" @click="goFirst" :disabled="loading || page <= 1">{{ t('first') }}</button>
                    <button type="button" class="pager-btn" @click="prevPage" :disabled="loading || page <= 1">{{ t('previous') }}</button>

                    <button
                        v-for="p in pageButtons"
                        :key="p"
                        type="button"
                        class="pager-num"
                        :class="{ active: p === page }"
                        @click="goPage(p)"
                        :disabled="loading"
                    >
                        {{ p }}
                    </button>

                    <button type="button" class="pager-btn" @click="nextPage" :disabled="loading || page >= totalPages">{{ t('next') }}</button>
                    <button type="button" class="pager-btn" @click="goLast" :disabled="loading || page >= totalPages">{{ t('last') }}</button>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <div v-if="confirmOpen" class="modal-backdrop" @click.self="closeConfirm">
                <div class="modal">
                    <div class="modal-header">
                        <div class="modal-title">{{ t('confirm_payment') }}</div>
                        <button type="button" class="modal-close" @click="closeConfirm">×</button>
                    </div>

                    <div class="modal-body">
                        <div class="modal-hint">{{ t('confirm_payment_hint') }}</div>

                        <div v-if="selected" class="modal-details">
                            <div class="detail-row">
                                <div class="detail-label">{{ t('customer') }}</div>
                                <div class="detail-value">{{ selected.CustomerName || '-' }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">{{ t('mobile') }}</div>
                                <div class="detail-value mono">{{ selected.MobileNo || '-' }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">{{ t('bill_no') }}</div>
                                <div class="detail-value mono">{{ selected.BillNo || '-' }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">{{ t('amount') }}</div>
                                <div class="detail-value mono">{{ formatAmount(selected.Amount) }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">{{ t('payment_method') }}</div>
                                <div class="detail-value">{{ selected.Payment_Method || '-' }}</div>
                            </div>
                            <div class="detail-row">
                                <div class="detail-label">{{ t('trx') }}</div>
                                <div class="detail-value mono">{{ selected.Transaction_ID || '-' }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeConfirm">{{ t('cancel') }}</button>
                        <button type="button" class="btn btn-primary" @click="confirmSelected" :disabled="selectedBusy">{{ t('confirm') }}</button>
                    </div>
                </div>
            </div>
        </Teleport>

        <Teleport to="body">
            <div class="toast-container">
                <TransitionGroup name="toast" tag="div">
                    <div v-for="toast in toasts" :key="toast.id" :class="['toast', `toast-${toast.type}`]">
                        <div class="toast-content">
                            <p class="toast-message">{{ toast.message }}</p>
                        </div>
                        <button @click="removeToast(toast.id)" class="toast-close">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </TransitionGroup>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from 'vue';

export default {
    name: 'BillingPage',
    setup() {
        const adminT = inject('adminT', (k) => k);
        const adminLang = inject('adminLang', ref('en'));
        const status = ref('all');
        const searchQuery = ref('');
        const items = ref([]);
        const pagination = ref({ page: 1, per_page: 20, total: 0 });
        const busyIds = ref({});
        const toasts = ref([]);
        const loading = ref(true);
        let searchTimer = null;

        const page = ref(1);
        const perPage = ref(20);
        const lastUpdatedAt = ref(null);
        const showFilters = ref(true);
        const colFilters = ref({
            billNo: '',
            billType: '',
            customer: '',
            amount: '',
            paymentDate: '',
            method: '',
            trx: '',
        });

        const confirmOpen = ref(false);
        const selected = ref(null);

        const t = (key) => adminT(key);
        const langValue = () => (adminLang?.value === 'bn' ? 'bn' : 'en');

        const load = async () => {
            loading.value = true;
            try {
                const res = await window.axios.get('/admin/api/payment-queue', {
                    params: {
                        status: status.value,
                        page: page.value,
                        per_page: perPage.value,
                        q: searchQuery.value || '',
                        lang: langValue(),
                    },
                });
                items.value = res.data?.data?.items || [];
                pagination.value = res.data?.data?.pagination || { page: 1, per_page: perPage.value, total: 0 };
                lastUpdatedAt.value = new Date();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                loading.value = false;
            }
        };

        const reloadAll = async () => {
            await load();
        };

        const setStatus = (value) => {
            status.value = value;
        };

        const openConfirm = (row) => {
            selected.value = row || null;
            confirmOpen.value = true;
        };

        const closeConfirm = () => {
            confirmOpen.value = false;
            selected.value = null;
        };

        const selectedBusy = computed(() => {
            const id = selected.value?.BillPaymentID;
            if (!id) return false;
            return busyIds.value?.[id] === true;
        });

        const confirmSelected = async () => {
            const id = selected.value?.BillPaymentID;
            if (!id) return;
            busyIds.value = { ...busyIds.value, [id]: true };
            try {
                const res = await window.axios.post(`/admin/api/payment-queue/${id}/confirm`, { lang: langValue() });
                addToast('success', res.data?.message || t('success'));
                closeConfirm();
                await reloadAll();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [id]: false };
            }
        };

        const filteredItems = computed(() => {
            const f = colFilters.value || {};

            const normalize = (v) => (v ?? '').toString().toLowerCase();
            const hasCol =
                !!(f.billNo || '').trim()
                || !!(f.billType || '').trim()
                || !!(f.customer || '').trim()
                || !!(f.amount || '').trim()
                || !!(f.method || '').trim()
                || !!(f.trx || '').trim();

            if (!hasCol && !(f.paymentDate || '').toString().trim()) return items.value;

            const billNoQ = normalize(f.billNo).trim();
            const billTypeQ = normalize(f.billType).trim();
            const customerQ = normalize(f.customer).trim();
            const amountQ = normalize(f.amount).trim();
            const payDateQ = (f.paymentDate || '').toString().trim();
            const methodQ = normalize(f.method).trim();
            const trxQ = normalize(f.trx).trim();

            return items.value.filter((x) => {
                const billNo = normalize(x.BillNo);
                const billType = normalize(x.BillType);
                const customerName = normalize(x.CustomerName);
                const mobile = normalize(x.MobileNo);
                const trx = normalize(x.Transaction_ID);
                const method = normalize(x.Payment_Method);
                const amount = normalize(x.Amount);

                if (billNoQ && !billNo.includes(billNoQ)) return false;
                if (billTypeQ && !billType.includes(billTypeQ)) return false;
                if (customerQ && !(customerName.includes(customerQ) || mobile.includes(customerQ))) return false;
                if (methodQ && !method.includes(methodQ)) return false;
                if (trxQ && !trx.includes(trxQ)) return false;
                if (amountQ && !amount.includes(amountQ)) return false;

                if (payDateQ) {
                    const ts = x.Payment_Date ? Date.parse(x.Payment_Date) : NaN;
                    if (!Number.isFinite(ts)) return false;
                    const iso = new Date(ts).toISOString().slice(0, 10);
                    if (iso !== payDateQ) return false;
                }

                return true;
            });
        });

        const hasAnyFilter = computed(() => {
            const f = colFilters.value || {};
            return !!(
                (f.billNo || '').trim()
                || (f.billType || '').trim()
                || (f.customer || '').trim()
                || (f.amount || '').trim()
                || (f.paymentDate || '').toString().trim()
                || (f.method || '').trim()
                || (f.trx || '').trim()
            );
        });

        const debouncedSearch = () => {
            if (searchTimer) clearTimeout(searchTimer);
            searchTimer = setTimeout(async () => {
                page.value = 1;
                await load();
            }, 350);
        };

        const toggleFilters = () => {
            showFilters.value = !showFilters.value;
        };

        const exportCsv = () => {
            const rows = filteredItems.value || [];
            const header = [
                'BillPaymentID',
                'BillNo',
                'BillType',
                'CustomerName',
                'MobileNo',
                'Amount',
                'Payment_Date',
                'Payment_Method',
                'Transaction_ID',
                'Payment_Status',
            ];
            const esc = (v) => {
                const s = (v ?? '').toString();
                if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) return `"${s.replace(/"/g, '""')}"`;
                return s;
            };
            const lines = [header.join(',')].concat(
                rows.map((r) =>
                    header
                        .map((k) => {
                            if (k === 'Payment_Status') return esc(r.Payment_Status === 0 ? 'Pending' : 'Confirmed');
                            return esc(r[k]);
                        })
                        .join(',')
                )
            );
            const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const stamp = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `billing_${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        };

        const printTable = () => {
            const rows = filteredItems.value || [];
            const html = `
                <html>
                    <head>
                        <title>Billing</title>
                        <style>
                            body { font-family: Arial, sans-serif; padding: 18px; }
                            h2 { margin: 0 0 12px; }
                            table { width: 100%; border-collapse: collapse; }
                            th, td { border: 1px solid #e2e8f0; padding: 8px 10px; font-size: 12px; }
                            th { background: #f7fafc; text-align: left; }
                        </style>
                    </head>
                    <body>
                        <h2>Customer Payment</h2>
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Bill No</th>
                                    <th>Bill Type</th>
                                    <th>Customer</th>
                                    <th>Amount</th>
                                    <th>Payment Date</th>
                                    <th>Method</th>
                                    <th>Trx</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${rows
                                    .map(
                                        (r) => `
                                    <tr>
                                        <td>${r.BillPaymentID ?? ''}</td>
                                        <td>${r.BillNo ?? ''}</td>
                                        <td>${r.BillType ?? ''}</td>
                                        <td>${(r.CustomerName ?? '') + ' ' + (r.MobileNo ? '(' + r.MobileNo + ')' : '')}</td>
                                        <td>${r.Amount ?? ''}</td>
                                        <td>${r.Payment_Date ?? ''}</td>
                                        <td>${r.Payment_Method ?? ''}</td>
                                        <td>${r.Transaction_ID ?? ''}</td>
                                        <td>${r.Payment_Status === 0 ? 'Pending' : 'Confirmed'}</td>
                                    </tr>`
                                    )
                                    .join('')}
                            </tbody>
                        </table>
                    </body>
                </html>
            `;
            const w = window.open('', '_blank', 'width=980,height=720');
            if (!w) {
                addToast('error', t('failed'));
                return;
            }
            w.document.open();
            w.document.write(html);
            w.document.close();
            w.focus();
            w.print();
            w.close();
        };

        const totalPages = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(pagination.value?.per_page || perPage.value || 20);
            return Math.max(1, Math.ceil(total / Math.max(1, size)));
        });

        const pageButtons = computed(() => {
            const total = totalPages.value;
            const current = page.value;
            const maxButtons = 5;
            if (total <= maxButtons) return Array.from({ length: total }, (_, i) => i + 1);
            const half = Math.floor(maxButtons / 2);
            let start = Math.max(1, current - half);
            let end = start + maxButtons - 1;
            if (end > total) {
                end = total;
                start = end - maxButtons + 1;
            }
            return Array.from({ length: end - start + 1 }, (_, i) => start + i);
        });

        const goPage = async (p) => {
            const target = Math.min(Math.max(1, Number(p) || 1), totalPages.value);
            if (target === page.value) return;
            page.value = target;
            await load();
        };

        const goFirst = async () => {
            await goPage(1);
        };

        const goLast = async () => {
            await goPage(totalPages.value);
        };

        const nextPage = async () => {
            if (page.value >= totalPages.value) return;
            page.value = page.value + 1;
            await load();
        };

        const prevPage = async () => {
            if (page.value <= 1) return;
            page.value = page.value - 1;
            await load();
        };

        const formatDateTime = (value) => {
            if (!value) return '-';
            try {
                return new Date(value).toLocaleString();
            } catch {
                return value;
            }
        };

        const formatAmount = (value) => {
            if (value === null || value === undefined || value === '') return '-';
            const n = Number(value);
            if (Number.isFinite(n)) return n.toLocaleString();
            return String(value);
        };

        const lastUpdatedText = computed(() => {
            if (!lastUpdatedAt.value) return '-';
            try {
                return lastUpdatedAt.value.toLocaleString();
            } catch {
                return '-';
            }
        });

        const showingText = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(pagination.value?.per_page || perPage.value || 20);
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const from = total === 0 ? 0 : (current - 1) * size + 1;
            const to = total === 0 ? 0 : (current - 1) * size + Math.min(size, filteredItems.value?.length || 0);
            const tpl = t('showing_entries');
            const base = tpl && tpl !== 'showing_entries' && tpl.includes('{from}')
                ? tpl.replace('{from}', String(from)).replace('{to}', String(Math.min(to, total))).replace('{total}', String(total))
                : `Showing ${from} to ${Math.min(to, total)} of ${total} entries`;

            if (!hasAnyFilter.value) return base;
            const filteredTpl = t('filtered');
            const suffix = filteredTpl && filteredTpl !== 'filtered' && filteredTpl.includes('{count}')
                ? filteredTpl.replace('{count}', String(filteredItems.value.length))
                : `(filtered: ${filteredItems.value.length})`;
            return `${base} ${suffix}`;
        });

        const addToast = (type, message) => {
            const id = Date.now() + Math.random();
            toasts.value.push({ id, type, message });
            setTimeout(() => removeToast(id), 4500);
        };

        const removeToast = (id) => {
            toasts.value = toasts.value.filter((t) => t.id !== id);
        };

        watch(status, async () => {
            page.value = 1;
            await load();
        });

        watch(perPage, async () => {
            page.value = 1;
            await load();
        });

        onMounted(load);

        return {
            status,
            searchQuery,
            items,
            pagination,
            busyIds,
            toasts,
            loading,
            page,
            perPage,
            totalPages,
            pageButtons,
            goPage,
            goFirst,
            goLast,
            lastUpdatedText,
            colFilters,
            showFilters,
            toggleFilters,
            exportCsv,
            printTable,
            showingText,
            confirmOpen,
            selected,
            selectedBusy,
            t,
            load,
            reloadAll,
            setStatus,
            openConfirm,
            closeConfirm,
            confirmSelected,
            filteredItems,
            nextPage,
            prevPage,
            debouncedSearch,
            hasAnyFilter,
            formatDateTime,
            formatAmount,
            removeToast,
        };
    },
};
</script>

<style scoped>
.datatable-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 10px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
}

.length-select {
    width: 64px;
    height: 32px;
    border-radius: 6px;
    border: 2px solid #2563eb;
    color: #1e40af;
    font-weight: 800;
    background: #ffffff;
    padding: 4px 6px;
    outline: none;
}

.tool-btn {
    height: 32px;
    padding: 0 10px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #0f172a;
    font-weight: 800;
    font-size: 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tool-btn-icon {
    width: 36px;
    padding: 0;
    justify-content: center;
}

.tool-btn-primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #ffffff;
}

.tool-btn-excel {
    background: #ffffff;
    border-color: #fb923c;
    color: #fb923c;
    border-width: 2px;
}

.tool-btn-light {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #0f172a;
}

.tool-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.search-box {
    position: relative;
    width: 320px;
    max-width: 100%;
}

.search-box svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-input {
    width: 100%;
    height: 34px;
    border-radius: 8px;
    border: 2px solid #2563eb;
    padding: 0 12px 0 42px;
    font-size: 13px;
    outline: none;
}

.table-container {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
}

.data-table thead {
    background: #0b2c3d;
}

.data-table thead th {
    color: #ffffff;
    padding: 10px 8px;
    font-weight: 900;
    font-size: 12px;
    text-align: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    vertical-align: top;
    white-space: nowrap;
}

.th-input,
.th-select {
    width: 100%;
    height: 30px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.9);
    background: #ffffff;
    padding: 0 8px;
    font-weight: 700;
    font-size: 12px;
    outline: none;
}

.th-select {
    padding-right: 8px;
}

.col-amount {
    text-align: right;
}

.col-amount .th-input {
    text-align: right;
}

.col-amount .th-select {
    text-align: right;
}

.th-date {
    padding-right: 6px;
}

.data-table td {
    padding: 10px 8px;
    border-top: 1px solid #e2e8f0;
    vertical-align: top;
}

.data-table thead th:first-child {
    width: 56px;
}

.data-table td:first-child {
    width: 56px;
}

.action-buttons .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 10px;
    font-weight: 800;
}

.data-table tbody tr:hover td {
    background: rgba(226, 232, 240, 0.55);
}

.data-table tbody tr:nth-child(even) td {
    background: rgba(241, 245, 249, 0.55);
}

.billing-actions {
    align-items: flex-start;
}

.status-tabs {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.status-tab {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: rgba(255, 255, 255, 0.9);
    color: var(--admin-text);
    font-weight: 800;
    cursor: pointer;
}

.status-tab.active {
    background: rgba(15, 23, 42, 0.92);
    color: #ffffff;
    border-color: rgba(15, 23, 42, 0.92);
}

.count-pill {
    min-width: 34px;
    height: 24px;
    padding: 0 10px;
    border-radius: 999px;
    background: rgba(100, 116, 139, 0.14);
    color: inherit;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
    font-size: 12px;
}

.status-tab.active .count-pill {
    background: rgba(255, 255, 255, 0.16);
}

.compact-controls {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
}

.per-page {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: rgba(255, 255, 255, 0.9);
}

.per-page-label {
    color: var(--admin-muted);
    font-weight: 800;
    font-size: 12px;
}

.per-page-select {
    width: 84px;
    padding: 8px 10px;
    border-radius: 999px;
}

.billing-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 14px;
}

.table-controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}

.date-filter {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.date-field {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: rgba(255, 255, 255, 0.9);
}

.date-label {
    color: var(--admin-muted);
    font-weight: 800;
    font-size: 12px;
}

.date-input {
    border: none;
    background: transparent;
    color: var(--admin-text);
    font-weight: 800;
    font-size: 12px;
    outline: none;
}

.clear-btn {
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: rgba(255, 255, 255, 0.9);
    color: var(--admin-text);
    padding: 9px 12px;
    border-radius: 999px;
    font-weight: 900;
    cursor: pointer;
}

.clear-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.filter-row th {
    background: rgba(248, 250, 252, 0.8);
}

.filter-cell {
    padding: 10px 10px;
}

.filter-input {
    width: 100%;
    padding: 8px 10px;
    border-radius: 10px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: rgba(255, 255, 255, 0.9);
    color: var(--admin-text);
    font-weight: 700;
    outline: none;
}

.summary-card {
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 18px;
    background: var(--admin-card-bg);
    padding: 14px 14px;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.06);
}

.summary-card-pending {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(255, 255, 255, 0.9));
}

.summary-card-confirmed {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(255, 255, 255, 0.9));
}

.summary-card-all {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(255, 255, 255, 0.9));
}

.summary-card-meta {
    background: rgba(255, 255, 255, 0.7);
}

.summary-label {
    color: var(--admin-muted);
    font-weight: 800;
    font-size: 12px;
}

.summary-value {
    margin-top: 8px;
    font-size: 26px;
    font-weight: 950;
    color: var(--admin-text);
    line-height: 1.1;
}

.summary-meta {
    font-size: 12px;
    font-weight: 800;
    color: var(--admin-text);
}

.pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 8px 12px;
    flex-wrap: wrap;
}

.pager-left {
    color: var(--admin-muted);
    font-weight: 800;
    font-size: 12px;
}

.pager-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.pager-btn {
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: var(--admin-text);
    padding: 7px 10px;
    border-radius: 6px;
    font-weight: 800;
    cursor: pointer;
    min-width: 66px;
}

.pager-num {
    min-width: 44px;
    height: 32px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: var(--admin-text);
    border-radius: 6px;
    font-weight: 900;
    cursor: pointer;
}

.pager-num.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.pager-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.skeleton-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.skeleton-row {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.7), rgba(226, 232, 240, 0.25), rgba(226, 232, 240, 0.7));
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
    0% { background-position: 0% 0%; }
    100% { background-position: -200% 0%; }
}

.modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(2, 6, 23, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
}

.modal {
    width: 100%;
    max-width: 520px;
    border-radius: 18px;
    background: var(--admin-card-bg);
    border: 1px solid rgba(15, 23, 42, 0.12);
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
    overflow: hidden;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.modal-title {
    font-weight: 950;
    color: var(--admin-text);
    font-size: 16px;
}

.modal-close {
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 20px;
    color: var(--admin-muted);
    line-height: 1;
}

.modal-body {
    padding: 14px 16px;
}

.modal-hint {
    color: var(--admin-muted);
    font-weight: 700;
    font-size: 13px;
    margin-bottom: 12px;
}

.modal-details {
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: var(--admin-soft-bg);
    border-radius: 14px;
    padding: 12px 12px;
}

.detail-row {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    color: var(--admin-muted);
    font-size: 12px;
    font-weight: 800;
}

.detail-value {
    color: var(--admin-text);
    font-size: 13px;
    font-weight: 800;
}

.modal-footer {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    padding: 14px 16px;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
}

@media (max-width: 1024px) {
    .search-box {
        width: 100%;
    }
}
</style>
